import React, { FC } from 'react';
import styled from 'styled-components';
import { Alert, Result } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { IBcfImportResult } from 'Data/BcfImport.api';

interface IStep5ExecuteProps {
  importProgress: number;
  isImporting: boolean;
  importResult: IBcfImportResult | undefined;
  error: string | undefined;
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  margin: 0;
`;

const Description = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
`;

const LoadingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 20px;
`;

const LoadingIcon = styled(LoadingOutlined)`
  font-size: 48px;
  color: ${({ theme }) => theme.colors.primary.primary};
`;

const LoadingText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
`;

const QueuedSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 20px;
  background-color: ${({ theme }) => theme.colors.surface.surfaceContainer};
  border-radius: 8px;
  text-align: center;
`;

const QueuedIcon = styled(ClockCircleOutlined)`
  font-size: 48px;
  color: ${({ theme }) => theme.colors.primary.primary};
`;

const QueuedTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.surface.onSurface};
  margin: 0;
`;

const QueuedMessage = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  margin: 0;
  max-width: 400px;
`;

const TopicCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: ${({ theme }) => theme.colors.primary.primaryContainer};
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.primary.primary};
`;

const SuccessIcon = styled(CheckCircleOutlined)`
  font-size: 20px;
  color: ${({ theme }) => theme.colors.success.success};
`;

const FailureIcon = styled(CloseCircleOutlined)`
  font-size: 48px;
  color: ${({ theme }) => theme.colors.danger.danger};
`;

const ResultList = styled.ul`
  margin: 0;
  padding-left: 18px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.surface.onSurfaceVariant};
  text-align: left;
  max-height: 180px;
  overflow-y: auto;
`;

const Step5Execute: FC<IStep5ExecuteProps> = ({
  importProgress,
  isImporting,
  importResult,
  error,
}) => {
  return (
    <Container>
      <Header>
        <Title>Importação</Title>
        <Description>Status da importação do arquivo BCF</Description>
      </Header>

      {error && <Alert message={error} type="error" showIcon />}

      {isImporting && (
        <LoadingSection>
          <LoadingIcon spin />
          <LoadingText>Enviando para processamento...</LoadingText>
        </LoadingSection>
      )}

      {importResult?.queued && (
        <QueuedSection>
          <QueuedIcon />
          <QueuedTitle>Importação iniciada!</QueuedTitle>
          <TopicCount>
            <SuccessIcon />
            {importResult.totalTopics} tópico(s) na fila
          </TopicCount>
          <QueuedMessage>
            {importResult.message || 'A importação está sendo processada em segundo plano. Você receberá uma notificação quando concluir.'}
          </QueuedMessage>
          <Alert
            message="Você pode fechar esta janela"
            description="A importação continuará em segundo plano. Clique na notificação para ver o resultado."
            type="info"
            showIcon
            style={{ marginTop: 16, textAlign: 'left' }}
          />
        </QueuedSection>
      )}

      {/* Lote recusado na hora: o backend respondeu sem enfileirar nada. */}
      {importResult && !importResult.queued && !importResult.success && (
        <QueuedSection>
          <FailureIcon />
          <QueuedTitle>A importação falhou</QueuedTitle>
          <QueuedMessage>
            {importResult.message || 'Nenhuma atividade foi criada.'}
          </QueuedMessage>
          {importResult.errors?.length > 0 && (
            <ResultList>
              {importResult.errors.map((message, index) => (
                <li key={index}>{message}</li>
              ))}
            </ResultList>
          )}
        </QueuedSection>
      )}

      {/* Lote processado de forma síncrona, com parte dos tópicos recusados. */}
      {importResult && !importResult.queued && importResult.success && importResult.totalFailed > 0 && (
        <>
          <Alert
            message={`${importResult.totalImported} de ${importResult.totalTopics} tópico(s) importado(s)`}
            description={`${importResult.totalFailed} tópico(s) não foram criados.`}
            type="warning"
            showIcon
          />
          {importResult.failedItems?.length > 0 && (
            <ResultList>
              {importResult.failedItems.map((item) => (
                <li key={item.id}>{`${item.title}: ${item.message}`}</li>
              ))}
            </ResultList>
          )}
        </>
      )}

      {!isImporting && !importResult && !error && (
        <Result
          icon={<ClockCircleOutlined />}
          title="Pronto para importar"
          subTitle="Clique no botão 'Importar' para iniciar o processo."
        />
      )}
    </Container>
  );
};

export default Step5Execute;
